import { useShoppingcartStore } from "@features/cart/shopping-cart-store";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useAuth } from "./context-auth";
import type { Product } from "@type/product";
import type { AllergyType, ProductType } from "@type/global";
import {
  categoriesQuery,
  deliveryAreaQuery,
  issuersQuery,
  prefetchCategories,
  prefetchDeliveryArea,
  prefetchIssuers,
  prefetchProducts,
  productsQuery,
  statiegeldQuery,
} from "@provider/app-data-queries";
import { useAppState } from "@utils";
import { allowedZipcodes } from "../data";
import { useStockRefresh } from "@hooks/use-stock-refresh";
import { setProductImageVersions } from "@provider/image-url";

type AppDataType = ReturnType<typeof useAppData>;
const AppDataContext = React.createContext<AppDataType>(undefined as any);
export const AppDataProvider = ({ children }: { children: React.ReactNode }) => {
  const values = useAppData();

  return <AppDataContext.Provider value={values}>{children}</AppDataContext.Provider>;
};

// prefetch queries before first render start
prefetchCategories();
prefetchProducts();
prefetchIssuers();
prefetchDeliveryArea();

function useAppData() {
  const { token } = useAuth();
  const load = useShoppingcartStore((state) => state.load);

  const appState = useAppState();
  const enabled = appState === "active";
  React.useEffect(() => {
    if (token && enabled) {
      load();
    }
  }, [token, enabled]);

  const categories = useQuery({ ...categoriesQuery });
  const products = useQuery({ ...productsQuery });
  const issuers = useQuery({ ...issuersQuery });
  const deliveryArea = useQuery({ ...deliveryAreaQuery });
  const statiegeld = useQuery({ ...statiegeldQuery, enabled: !!token, refetchOnWindowFocus: true });

  React.useEffect(() => {
    if (appState === "active") {
      categories.refetch();
      products.refetch();
      issuers.refetch();
      if (token) {
        statiegeld.refetch();
      }
    }
  }, [appState]);

  const productData = React.useMemo(() => {
    // pid -> image_version voor de plekken die alleen een pid kennen
    setProductImageVersions(products.data);
    return simplifyProducts(products.data || []);
  }, [products.data]);
  useStockRefresh(!!token, products.data || [], productsQuery.queryKey);

  return {
    categoryQuery: categories,
    productQuery: {
      ...products,
      data: productData,
    },
    issuersQuery: issuers,
    statiegeldQuery: statiegeld,
    deliveryArea: deliveryArea.data?.nl?.length ? deliveryArea.data : allowedZipcodes,
  };
}

export function useCategories() {
  const { categoryQuery } = React.useContext(AppDataContext);
  return { ...categoryQuery, data: categoryQuery.data || [] };
}

// Bezorggebied: van de server, met de ingebouwde lijst als terugval.
export function useDeliveryArea() {
  const ctx = React.useContext(AppDataContext);
  return ctx?.deliveryArea ?? allowedZipcodes;
}

export function useStatiegeld() {
  const { statiegeldQuery } = React.useContext(AppDataContext);
  return statiegeldQuery;
}

export function simplifyProducts(data: undefined | Product[]): ProductType[] {
  return (
    data
      ?.filter((it) => !!it)
      .map((product) => {
        const voedingswaardenSimpeler = product.voedingswaarden?.split("$").map((v) => {
          const [label, value] = v.split("#");
          return { label: label || "", value: value || "" };
        });

        return {
          ...product,
          voedingswaardenSimpeler,
          allergieSimpeler: allergies.filter((a, i) => {
            const mask = Math.pow(2, i);
            return !!(product.allergiecode & mask);

            // return !!(product?.allergiecode && mask);
          }),
        };
      }) || []
  );
}

export function simplifyProduct(product: Product): ProductType {
  const voedingswaardenSimpeler = product.voedingswaarden?.split("$").map((v) => {
    const [label, value] = v.split("#");
    return { label: label || "", value: value || "" };
  });

  return {
    ...product,
    voedingswaardenSimpeler,
    allergieSimpeler: allergies.filter((a, i) => {
      const mask = Math.pow(2, i - 1);
      return !!(product?.allergiecode && mask);
    }),
  };
}

export function useIssuersList() {
  const { issuersQuery } = React.useContext(AppDataContext);
  return issuersQuery;
}

export function useProducts() {
  const { productQuery } = React.useContext(AppDataContext);
  return productQuery;
}

export function useProduct(id: number) {
  const productQuery = useProducts();
  const product = productQuery.data.find((p) => p.id === id);
  return {
    ...productQuery,
    data: product,
  };
}

export function useDiscountProducts() {
  const { productQuery } = React.useContext(AppDataContext);
  return {
    ...productQuery,
    data: productQuery.data.filter((p) => p.actieprijs > 0 && p.actie > 0),
  };
}

export const allergies: AllergyType[] = [
  { id: 1, name: "Eivrij", icon: "eivrij.png" },
  { id: 2, name: "Schaaldiervrij", icon: "schaaldiervrij.png" },
  { id: 3, name: "Lupinevrij", icon: "lupinevrij.png" },
  { id: 4, name: "Selderijvrij", icon: "selderijvrij.png" },
  {
    id: 5,
    name: "Melk- en lactosevrij",
    icon: "melk-en-lactosevrij.png",
  },
  { id: 6, name: "Sesamvrij", icon: "sesamvrij.png" },
  { id: 7, name: "Mosterdvrij", icon: "mosterdvrij.png" },
  {
    id: 8,
    name: "Pinda- en notenvrij",
    icon: "pinda-en-notenvrij.png",
  },
  { id: 9, name: "Sojavrij", icon: "sojavrij.png" },
  { id: 10, name: "Visvrij", icon: "visvrij.png" },
  { id: 11, name: "Sulfietvrij", icon: "sulfietvrij.png" },
  { id: 12, name: "Weekdiervrij", icon: "weekdiervrij.png" },
  {
    id: 13,
    name: "Tarwe- en glutenvrij",
    icon: "tarwe-en-glutenvrij.png",
  },
  { id: 14, name: "Vegetarisch", icon: "vegetarisch.png" },
  { id: 15, name: "Veganistisch", icon: "veganistisch.png" },
];

export function useIngredientsList({ ingredientProductPids }: { ingredientProductPids: number[] }) {
  const { data: products } = useProducts();

  return products.filter((prod) => {
    if (ingredientProductPids.includes(prod.pid)) {
      return prod;
    }
  });
}
